/**
 * Copyright (c) 2026, CheckPlus Limited
 */
import React, { Component } from "react";
import "./DonationRecordReauthenticate.css";
import { simpleFetch } from "../../restWrapper";
import InfiniteScroll from "react-infinite-scroll-component";
import { Link } from "react-router-dom";
import PropTypes from "prop-types";
import { NumericFormat } from "react-number-format";
import { Helmet } from "react-helmet";
import { getOrganizationID, validatePlatformMatch, resolveThemeColors } from "../../assets/helper";
import { PLATFORM } from "../../config";

class DonationRecordReauthenticate extends Component {
  constructor() {
    super();
    const subdomain = getOrganizationID(window.location.href).subdomain;
    const domain = getOrganizationID(window.location.href).domain;
    const isCustomDomain = getOrganizationID(window.location.href).isCustomDomain;
    this.state = {
      fetching: false,
      subdomain: subdomain,
      domain: domain,
      isCustomDomain: isCustomDomain,
      buttonColor: "",
      headerColor: "",
      orgId: "",
      orgName: "",
      logo: "",
      paymentRecords: [],
      lastId: 0,
      frequencyMenuOpen: false,
    };
    this.fetchData = this.fetchData.bind(this);
    this.toggleFrequencyMenu = this.toggleFrequencyMenu.bind(this);
  }
  componentDidMount() {
    simpleFetch("/donors/organization", "GET", {
      subDomain: this.state.isCustomDomain ? this.state.domain : this.state.subdomain,
      isCustomDomain: this.state.isCustomDomain,
    }).then((res) => {
      // Validate platform matches organization type
      const platformMatch = validatePlatformMatch(
        res.body.organizationObj.organizationType,
        PLATFORM
      );

      if (!platformMatch) {
        // console.error("Cannot display donation records: platform mismatch");
        this.props.history.push({ pathname: "/" + this.props.language });
        return;
      }

      this.setState({
        ...this.state,
        ...{
          buttonColor: res.body.organizationObj.buttonColor,
          headerColor: res.body.organizationObj.headerColor,
          orgId: res.body.id,
          orgName: res.body.organizationObj.organizationName,
          logo: res.body.organizationObj.logo,
        },
      }, () => {
        // Callback ensures orgId is set before fetching data (React 18 fix)
        this.fetchData();
      });
    });
  }

  fetchData() {
    if (this.state.fetching) return false;
    if (!this.state.orgId) return false;
    this.setState({
      ...this.state,
      ...{
        fetching: true,
      },
    });

    let findParams = {
      organizationViewId: this.state.orgId,
      status: "reauthenticate",
    };

    if (this.state.lastId) findParams.lastId = this.state.lastId;

    simpleFetch("/donors/payments", "GET", findParams).then((res) => {
      this.setState({
        ...this.state,
        ...{
          fetching: false,
        },
      });
      //    console.log(res.body);

      if (res.body.length === 0) {
        this.setState({
          ...this.state,
          ...{
            hasMore: false,
          },
        });
        return false;
      }
      const lastItem = res.body[res.body.length - 1];
      this.setState({
        lastId: lastItem.id,
        hasMore: true,
        paymentRecords: this.state.paymentRecords.concat(res.body),
      });
    });
  }

  toggleFrequencyMenu() {
    this.setState((prev) => ({
      frequencyMenuOpen: !prev.frequencyMenuOpen,
    }));
  }

  renderRecord(func, data) {
    const itemKey = "paymentID" + data.id;

    const rDate = new Date(data.scheduledDate);

    const d = rDate.getDate();
    const m = rDate.getMonth() + 1;
    const y = rDate.getFullYear();
    const h = rDate.getHours();
    const min = (rDate.getMinutes() < 10 ? "0" : "") + rDate.getMinutes();

    const date = y + "/" + m + "/" + d + " " + h + ":" + min;

    function onClickRecord() {
      func.props.history.push({
        pathname: "/DonationInfoReauthenticate",
        state: {
          buttonColor: func.state.buttonColor,
          headerColor: func.state.headerColor,
          orgName: func.state.orgName,
          paymentId: data.id,
        },
      });
    }

    const languageObj = this.props.languageObj.renderRecord;

    const hasRawHeaderColor =
      typeof this.state.headerColor === "string" &&
      this.state.headerColor.length > 0;
    const resolved = hasRawHeaderColor
      ? resolveThemeColors(this.state.headerColor, this.state.buttonColor)
      : { headerColor: "", buttonColor: "" };
    const hasHeaderColor = hasRawHeaderColor;
    const accentColor = hasHeaderColor
      ? `#${resolved.headerColor}`
      : "#606266";
    const accentColorSoft = hasHeaderColor
      ? `#${resolved.headerColor}1A`
      : "#6062661A";

    const buttonColor =
      resolved.buttonColor && resolved.buttonColor.length > 0
        ? `#${resolved.buttonColor}`
        : accentColor;

    const headerStyle = {
      "--donation-frame-accent": accentColor,
      "--donation-frame-accent-soft": accentColorSoft,
      "--donation-arrow-color": accentColor,
      "--donation-arrow-hover-color": buttonColor,
      "--donation-text-color": accentColor,
      "--donation-logo-color": accentColor,
    };

    const arrowLabel =
      (languageObj && languageObj.reauthenticate) || "Re-authenticate";
    const amountLabel =
      (languageObj && languageObj.amount) || "Amount";

    const logoStyle = {
      backgroundImage: "url(" + this.state.logo + ")",
      backgroundPosition: "center",
      backgroundRepeat: "no-repeat",
      backgroundSize: "contain",
      border: "none",
    };

    return (
      <div
        className="DonationRecordReauthenticate-frame"
        key={itemKey}
        onClick={onClickRecord}
        style={headerStyle}
      >
        <div className="DonationRecordReauthenticate-cardHeader">
          <div className="DonationRecordReauthenticate-statusBadge">
            <span role="img" aria-label="reauthenticate">
              🔒
            </span>
            {languageObj.reauthenticationRequired ||
              languageObj.reauthenticate ||
              "Re-authentication required"}
          </div>
          <div className="DonationRecordReauthenticate-dateTime">{date}</div>
        </div>

        <div className="DonationRecordReauthenticate-cardContent">
          <div className="DonationRecordReauthenticate-logoWrapper">
            <div className="DonationRecordReauthenticate-logo" style={logoStyle}></div>
          </div>
          <div className="DonationRecordReauthenticate-transactionInfo">
            <div className="DonationRecordReauthenticate-title">
              {func.state.orgName} - {data.projectName}
            </div>

            <div className="DonationRecordReauthenticate-amountRow">
              <div className="DonationRecordReauthenticate-amount">
                {amountLabel + ":"} HK
                <NumericFormat
                  decimalSeparator="."
                  displayType="text"
                  valueIsNumericString={true}
                  value={data.amount}
                  allowNegative={false}
                  thousandSeparator={true}
                  prefix={"$"}
                  fixedDecimalScale={true}
                  decimalScale={2}
                />
              </div>
              <div className="DonationRecordReauthenticate-arrowPill">
                <span>{arrowLabel}</span>
                <div className="DonationRecordReauthenticate-nextArrow" />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  render() {
    if (!this.state.headerColor) {
      return null;
    }

    const languageObj = this.props.languageObj;

    const scrollBarStyle = {
      overflow: "inherit",
      marginBottom: "100px",
    };

    const headerStyle = {
      background: "#" + this.state.headerColor,
    };

    return (
      <div className="DonationRecordReauthenticate-view">
        <Helmet>
          <title>{this.state.orgName}</title>
        </Helmet>
        <div className="DonationRecordReauthenticate-bg" style={headerStyle} />
        <div className="DonationRecordReauthenticate-container">
          <div className="DonationRecordReauthenticate-header">
            <Link to="/">
              <div className="DonationRecordReauthenticate-backContainer">
                <div className="DonationRecordReauthenticate-back">
                  <div className="DonationRecordReauthenticate-backBtn"></div>
                  <div className="DonationRecordReauthenticate-backText">
                    {languageObj.backTxt}
                  </div>
                </div>
              </div>
            </Link>

            <div className={`DonationRecordReauthenticate-frequencyContainer ${this.state.frequencyMenuOpen ? "is-open" : ""}`}>
              <Link to="/" className="DonationRecordReauthenticate-frequencyBack">
                <div className="DonationRecordReauthenticate-frequencyBackIcn" />
                <span>{languageObj.backTxt}</span>
              </Link>
              <button
                type="button"
                className="DonationRecordReauthenticate-frequencyToggle"
                onClick={this.toggleFrequencyMenu}
                aria-expanded={this.state.frequencyMenuOpen}
                aria-label={languageObj.statusToggle || "Toggle status filter"}
              >
                <div className="DonationRecordReauthenticate-reauthIcn" />
                <span>{languageObj.reauthenticate || "Re-authenticate"}</span>
                <span className="DonationRecordReauthenticate-frequencyToggleIcon" />
              </button>
              <div className="DonationRecordReauthenticate-frequencyCurrent">
                {languageObj.reauthenticate || "Re-authenticate"}
              </div>
              <div className="DonationRecordReauthenticate-frequency">
                <Link
                  to="/DonationRecord"
                  className="DonationRecordReauthenticate-tickGroup"
                >
                  <div className="DonationRecordReauthenticate-tickIcn" />
                  <div className="DonationRecordReauthenticate-tickTxt">
                    {languageObj.completed}
                  </div>
                </Link>

                <Link
                  to="/DonationRecordRecurring"
                  className="DonationRecordReauthenticate-scheduleGroup"
                >
                  <div className="DonationRecordReauthenticate-scheduleIcn" />
                  <div className="DonationRecordReauthenticate-scheduleTxt">
                    {languageObj.scheduled}
                  </div>
                </Link>

                <div className="DonationRecordReauthenticate-reauthGroup">
                  <div className="DonationRecordReauthenticate-reauthIcn" />
                  <div className="DonationRecordReauthenticate-reauthTxt">
                    {languageObj.reauthenticate}
                  </div>
                </div>
              </div>
            </div>

            <div className="DonationRecordReauthenticate-btn">{languageObj.btn}</div>
          </div>

          <div className="DonationRecordReauthenticate-main">
            <InfiniteScroll
              dataLength={this.state.paymentRecords.length}
              next={this.fetchData}
              hasMore={this.state.hasMore}
              style={scrollBarStyle}
            >
              {this.state.paymentRecords.map((record) =>
                this.renderRecord(this, record)
              )}
            </InfiniteScroll>
          </div>
        </div>
      </div>
    );
  }
}

DonationRecordReauthenticate.propTypes = {
  language: PropTypes.string,
  languageObj: PropTypes.object,
};

export default DonationRecordReauthenticate;
